import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiService } from '../services/api';

const Login = () => {
  const navigate = useNavigate();
  const { login, loading, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  React.useEffect(() => {
    if (error) {
      clearError();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Clear any stale errors on mount

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (error) {
      clearError();
    }
  };

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);

  const handleResendVerification = async () => {
    if (!formData.email) return;
    
    setResendLoading(true);
    setResendMessage(null);
    try {
      await ApiService.api.post('/users/send-email-verification', {
        identifier: formData.email
      });
      setResendMessage({ type: 'success', text: 'Verification email sent successfully! Please check your inbox.' });
    } catch (err) {
      setResendMessage({ type: 'error', text: err.response?.data?.message || 'Failed to resend verification email.' });
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResendMessage(null);
    
    try {
      await login(formData);
      navigate('/');
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ maxWidth: 400, mx: 'auto' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                Welcome Back
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to access the ISBAT LMS Student Portal
              </Typography>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3 }}
                action={
                  error.includes('Email not verified') && (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={handleResendVerification}
                      disabled={resendLoading}
                    >
                      {resendLoading ? 'SENDING...' : 'RESEND EMAIL'}
                    </Button>
                  )
                }
              >
                {error}
              </Alert>
            )}

            {resendMessage && (
              <Alert 
                severity={resendMessage.type} 
                sx={{ mb: 3 }}
                onClose={() => setResendMessage(null)}
              >
                {resendMessage.text}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  py: 1.5,
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#1565c0' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                onClick={() => navigate('/forgot-password')}
                sx={{ 
                  fontSize: '0.875rem',
                  color: '#1976d2',
                  textTransform: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                Forgot Password?
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                onClick={() => navigate('/')}
                sx={{ 
                  fontSize: '0.875rem',
                  color: '#666666',
                  textTransform: 'none',
                }}
              >
                Back to Home
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
